import { useState } from "react"
import { buildUrl } from "@planningcenter/cc-url"
import { Icon } from "source/shared/components"
import "../../styles/image-carousel.css"

interface CarouselStep {
  stepInstruction: React.ReactNode
  imageSrc: string
  imageAlt: string
}

export default function ImageCarousel() {
  const steps: CarouselStep[] = [
    {
      stepInstruction: (
        <>
          Send <strong>{`"GIVE $25"`}</strong> for a $25 donation to{" "}
          <strong>Carlsbad Church’s General</strong> fund.
        </>
      ),
      imageSrc: buildUrl("/images/text2give-step1.png"),
      imageAlt:
        "Mobile phone receiving text message response to 'GIVE $25' with instructions to proceed with donation",
    },
    {
      stepInstruction: (
        <>
          Click <strong>{`"Give now"`}</strong> on the confirmation screen.
        </>
      ),
      imageSrc: buildUrl("/images/text2give-step2.png"),
      imageAlt:
        "Mobile phone displaying a request to complete the $25 Text2Give donation in Church Center",
    },
    {
      stepInstruction: <>Receive a success message after submitting.</>,
      imageSrc: buildUrl("/images/text2give-step3.png"),
      imageAlt:
        "Mobile phone displaying a 'Thank you!' confirmation of the $25 Text2Give donation in Church Center",
    },
    {
      stepInstruction: (
        <>Receive a text confirmation when the donation is completed.</>
      ),
      imageSrc: buildUrl("/images/text2give-step4.png"),
      imageAlt:
        "Mobile phone receiving text message response with the completed $25 donation details",
    },
  ]

  const [currentIndex, setCurrentIndex] = useState(0)

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0
    const newIndex = isFirstSlide ? steps.length - 1 : currentIndex - 1
    setCurrentIndex(newIndex)
  }

  const goToNext = () => {
    const isLastSlide = currentIndex === steps.length - 1
    const newIndex = isLastSlide ? 0 : currentIndex + 1
    setCurrentIndex(newIndex)
  }

  return (
    <>
      <ol className="p-0 mb-0">
        {steps.map((step, index) => (
          <li
            key={index}
            className="step-instruction"
            aria-current={index === currentIndex ? "step" : undefined}
          >
            <strong>
              {index + 1}/{steps.length}
            </strong>
            <span>{step.stepInstruction}</span>
          </li>
        ))}
      </ol>
      <div className="carousel-wrapper" aria-label="Image carousel">
        <button
          className="carousel-nav"
          onClick={goToPrevious}
          aria-label="Previous"
          disabled={currentIndex === 0}
        >
          <Icon symbol={`general#left-chevron`} aria-hidden="true" />
        </button>
        <div className="carousel-track-container">
          <div
            className="carousel-track"
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {steps.map((step, index) => (
              <div className="carousel-slide" key={index}>
                <img src={step.imageSrc} alt={step.imageAlt} />
              </div>
            ))}
          </div>
        </div>
        <button
          className="carousel-nav"
          onClick={goToNext}
          aria-label="Next"
          disabled={currentIndex === steps.length - 1}
        >
          <Icon symbol={`general#right-chevron`} aria-hidden="true" />
        </button>
      </div>
      <div className="carousel-pagination">
        {steps.map((_, index) => (
          <button
            key={index}
            className={`carousel-page-btn ${index === currentIndex ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to step ${index + 1}`}
            aria-current={index === currentIndex ? "step" : undefined}
          />
        ))}
      </div>
    </>
  )
}
